import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Clock } from 'lucide-react';
import { onSessionExpired, resetSessionExpired, isSessionExpired } from '../sessionExpiry';
import { AUTH_STORAGE_KEYS } from '../constants';

/**
 * Global session-expired overlay. Mounted once in App.tsx, listens for
 * the session-expired event from any 401 API response, and renders a
 * blocking modal that forces re-login.
 *
 * Works for ALL roles (candidate, company, recruiter, admin) because
 * it sits above the router and doesn't depend on role-specific context.
 */
const SessionExpiredModal: React.FC = () => {
    const [show, setShow] = useState(isSessionExpired());
    const navigate = useNavigate();

    useEffect(() => {
        const unsub = onSessionExpired(() => setShow(true));
        return unsub;
    }, []);

    if (!show) return null;

    const handleSignIn = () => {
        // Clear all auth data
        Object.values(AUTH_STORAGE_KEYS).forEach((key) => {
            try { localStorage.removeItem(key); } catch {}
        });
        // Also clear any other auth-related keys
        try {
            localStorage.removeItem('authUser');
            localStorage.removeItem('authToken');
            localStorage.removeItem('companyData');
        } catch {}

        resetSessionExpired();
        setShow(false);
        navigate('/auth/login', { replace: true });
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
                {/* Top accent bar */}
                <div className="h-1 bg-gradient-to-r from-amber-400 via-orange-400 to-red-400" />

                <div className="px-8 pt-8 pb-6 text-center">
                    {/* Icon */}
                    <div className="w-16 h-16 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-5">
                        <Clock className="w-8 h-8 text-amber-500" />
                    </div>

                    {/* Title */}
                    <h2 className="text-xl font-bold text-gray-900 mb-2">
                        Session Expired
                    </h2>

                    {/* Message */}
                    <p className="text-sm text-gray-500 leading-relaxed mb-8">
                        You've been logged out due to inactivity. Please sign in again to continue where you left off.
                    </p>

                    {/* Action */}
                    <button
                        onClick={handleSignIn}
                        className="w-full flex items-center justify-center gap-2.5 px-6 py-3 bg-gray-900 text-white rounded-xl text-sm font-semibold hover:bg-gray-800 transition-colors shadow-sm"
                    >
                        <LogOut className="w-4 h-4" />
                        Sign In Again
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SessionExpiredModal;
